/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Grid,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    Flight,
    Hotel,
    DirectionsCar,
    Attractions,
    Add,
    Close,
} from '@mui/icons-material';
import { api } from '../utils/api';

interface AddTripItemDialogProps {
    open: boolean;
    onClose: () => void;
    tripId: string;
    tripDestination?: string;
    tripStartDate?: string;
    tripEndDate?: string;
    defaultType?: 'flight' | 'hotel' | 'car' | 'activity';
    onItemAdded?: (item: any) => void;
}

const itemTypeConfig = {
    flight: {
        icon: <Flight />,
        color: '#2196F3',
        label: 'Flight',
        titlePlaceholder: 'e.g., Flight to Paris',
        locationPlaceholder: 'e.g., JFK → CDG',
    },
    hotel: {
        icon: <Hotel />,
        color: '#4A6FA5',
        label: 'Hotel',
        titlePlaceholder: 'e.g., Hilton Garden Inn',
        locationPlaceholder: 'e.g., Downtown Paris',
    },
    car: {
        icon: <DirectionsCar />,
        color: '#607D8B',
        label: 'Car Rental',
        titlePlaceholder: 'e.g., Enterprise SUV',
        locationPlaceholder: 'e.g., Airport pickup',
    },
    activity: {
        icon: <Attractions />,
        color: '#2D6E6A',
        label: 'Activity',
        titlePlaceholder: 'e.g., Eiffel Tower Tour',
        locationPlaceholder: 'e.g., Champ de Mars',
    },
};

const AddTripItemDialog: React.FC<AddTripItemDialogProps> = ({
    open,
    onClose,
    tripId,
    tripDestination,
    tripStartDate,
    tripEndDate,
    defaultType,
    onItemAdded,
}) => {
    const [itemType, setItemType] = useState<'flight' | 'hotel' | 'car' | 'activity'>(defaultType || 'flight');
    const [title, setTitle] = useState('');
    const [location, setLocation] = useState(tripDestination || '');
    const [startDate, setStartDate] = useState(tripStartDate || '');
    const [endDate, setEndDate] = useState(tripEndDate || '');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [estimatedCost, setEstimatedCost] = useState('');
    const [notes, setNotes] = useState('');
    const [bookingUrl, setBookingUrl] = useState('');
    const [confirmationNumber, setConfirmationNumber] = useState('');
    const [itineraryDay, setItineraryDay] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const config = itemTypeConfig[itemType];

    const resetForm = () => {
        setTitle('');
        setLocation(tripDestination || '');
        setStartDate(tripStartDate || '');
        setEndDate(tripEndDate || '');
        setStartTime('');
        setEndTime('');
        setEstimatedCost('');
        setNotes('');
        setBookingUrl('');
        setConfirmationNumber('');
        setItineraryDay('');
        setError('');
    };

    const handleTypeChange = (_: React.MouseEvent<HTMLElement>, newType: string | null) => {
        if (newType) {
            setItemType(newType as any);
            // Auto-adjust dates for hotels
            if (newType === 'hotel') {
                setStartDate(tripStartDate || '');
                setEndDate(tripEndDate || '');
            }
        }
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            setError('Title is required');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await api.post(`/trips/${tripId}/items`, {
                item_type: itemType,
                title: title.trim(),
                location: location.trim() || undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                start_time: startTime || undefined,
                end_time: endTime || undefined,
                estimated_cost: estimatedCost ? parseFloat(estimatedCost) : undefined,
                notes: notes.trim() || undefined,
                booking_url: bookingUrl.trim() || undefined,
                confirmation_number: confirmationNumber.trim() || undefined,
                itinerary_day: itineraryDay ? parseInt(itineraryDay) : undefined,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                if (onItemAdded) onItemAdded(data.item);
                resetForm();
                onClose();
            } else {
                setError(data.error || 'Failed to add item');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Add sx={{ color: config.color }} />
                    Add {config.label}
                </Box>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
                )}

                {/* Type Selector */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        What are you adding?
                    </Typography>
                    <ToggleButtonGroup
                        value={itemType}
                        exclusive
                        onChange={handleTypeChange}
                        fullWidth
                        size="small"
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 1 } }}
                    >
                        <ToggleButton value="flight" sx={{ '&.Mui-selected': { bgcolor: '#E3F2FD', color: '#2196F3', borderColor: '#2196F3' } }}>
                            <Flight sx={{ mr: 0.5, fontSize: 18 }} /> Flight
                        </ToggleButton>
                        <ToggleButton value="hotel" sx={{ '&.Mui-selected': { bgcolor: '#F3E5F5', color: '#4A6FA5', borderColor: '#4A6FA5' } }}>
                            <Hotel sx={{ mr: 0.5, fontSize: 18 }} /> Hotel
                        </ToggleButton>
                        <ToggleButton value="car" sx={{ '&.Mui-selected': { bgcolor: '#ECEFF1', color: '#607D8B', borderColor: '#607D8B' } }}>
                            <DirectionsCar sx={{ mr: 0.5, fontSize: 18 }} /> Car
                        </ToggleButton>
                        <ToggleButton value="activity" sx={{ '&.Mui-selected': { bgcolor: '#E8F5F3', color: '#2D6E6A', borderColor: '#2D6E6A' } }}>
                            <Attractions sx={{ mr: 0.5, fontSize: 18 }} /> Activity
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Title */}
                <TextField
                    fullWidth
                    label={`${config.label} Name`}
                    placeholder={config.titlePlaceholder}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {/* Location */}
                <TextField
                    fullWidth
                    label="Location"
                    placeholder={config.locationPlaceholder}
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {/* Dates */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={itemType === 'hotel' ? 'Check-in' : 'Start Date'}
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={itemType === 'hotel' ? 'Check-out' : 'End Date'}
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>

                {/* Times (flights & activities) */}
                {(itemType === 'flight' || itemType === 'activity') && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={itemType === 'flight' ? 'Departure Time' : 'Start Time'}
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={itemType === 'flight' ? 'Arrival Time' : 'End Time'}
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Cost */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Estimated Cost"
                            type="number"
                            placeholder="0.00"
                            value={estimatedCost}
                            onChange={(e) => setEstimatedCost(e.target.value)}
                            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>$</Typography> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Itinerary Day"
                            type="number"
                            placeholder="e.g., 1"
                            value={itineraryDay}
                            onChange={(e) => setItineraryDay(e.target.value)}
                            inputProps={{ min: 1 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>

                {/* Confirmation & Booking URL */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Confirmation #"
                            placeholder="e.g., ABC123"
                            value={confirmationNumber}
                            onChange={(e) => setConfirmationNumber(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Booking URL"
                            placeholder="https://..."
                            value={bookingUrl}
                            onChange={(e) => setBookingUrl(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>

                {/* Notes */}
                <TextField
                    fullWidth
                    label="Notes"
                    placeholder="Any additional details..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '20px' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || !title.trim()}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Add />}
                    sx={{
                        bgcolor: config.color,
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { bgcolor: config.color, filter: 'brightness(0.9)' },
                    }}
                >
                    {saving ? 'Adding...' : `Add ${config.label}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTripItemDialog;
